import { adminUsersService } from './adminUsersService';
import { groupsService } from './groupsService';
import type { AdminUserResponse } from './adminUsersService';
import type { GroupResponse, GroupUserResponse } from './groupsService';
import type { 
  UserRecord, 
  UserGroup,
  GroupMember,
  InvitedUser, 
  UserFilters,
  UserGroupFilters,
  InvitedUserFilters,
  PaginatedResponse
} from '../types';

interface ApiResponse<T> {
  data: T;
  success: boolean;
  message?: string;
}

function mapBffUserToUserRecord(user: AdminUserResponse): UserRecord {
  let solicitud: 'Aprobada' | 'Pendiente' | 'Rechazada' = 'Pendiente';
  if (user.requestApproved === true) {
    solicitud = 'Aprobada';
  } else if (user.requestApproved === false) {
    solicitud = 'Rechazada';
  }

  return {
    id: user.id,
    username: user.username || '',
    firstName: user.firstName || '',
    lastName: user.lastName || '',
    registrationDate: user.registrationDate || 
      (user.createdTimestamp ? new Date(user.createdTimestamp).toISOString().split('T')[0] : ''),
    email: user.email || '',
    requestStatus: solicitud,
    userRole: mapUserRoleToDisplay(user.userRole),
    phone: user.phone || '',
    score: user.score ?? 0,
    groups: user.groups || [],
    adviser: user.adviser || '',
    enabled: user.enabled
  };
}

function mapBffGroupToUserGroup(group: GroupResponse, index: number): UserGroup {
  const colors = ['#652d86', '#7ab900', '#00d4ff', '#ff6b9d', '#ff9f43', '#5f7cff', '#20e3b2', '#9ca3af'];
  return {
    id: group.id,
    nombre: group.name,
    descripcion: group.attributes?.description || `Grupo ${group.name}`,
    color: group.attributes?.color || colors[index % colors.length],
    cantidadUsuarios: parseInt(group.attributes?.memberCount || '0', 10),
    fechaCreacion: group.attributes?.createdAt || new Date().toISOString().split('T')[0],
    estado: group.attributes?.enabled === 'false' ? 'Inactivo' : 'Activo'
  };
}

function mapGroupUserToMember(user: GroupUserResponse): GroupMember {
  return {
    id: user.id,
    username: user.username,
    email: user.email,
    firstName: user.firstName,
    lastName: user.lastName,
    enabled: user.enabled
  };
}

function mapUserRoleToDisplay(userRole: string | null): string {
  if (!userRole) return '';
  const roleMap: Record<string, string> = {
    'operator': 'Operador',
    'comissionist': 'Comisionista',
    'adviser': 'Asesor',
    'none': ''
  };
  return roleMap[userRole] || userRole;
}

function mapDisplayToUserRole(display: string): 'operator' | 'comissionist' | 'adviser' | 'none' {
  const roleMap: Record<string, 'operator' | 'comissionist' | 'adviser' | 'none'> = {
    'Operador': 'operator',
    'Comisionista': 'comissionist',
    'Asesor': 'adviser'
  };
  return roleMap[display] || 'none';
}

function mapSolicitudToRequestApproved(solicitud: string): boolean | undefined {
  if (solicitud === 'Aprobada') return true;
  if (solicitud === 'Rechazada') return false;
  return undefined;
}

export async function getUsers(
  page: number = 1,
  limit: number = 10,
  filters?: UserFilters
): Promise<PaginatedResponse<UserRecord>> {
  try {
    const bffPage = page - 1;
    const search = filters?.search || '';
    
    const response = await adminUsersService.listUsers(bffPage, limit, search);
    const data = response.data;
    
    let users = data.users.map(mapBffUserToUserRecord);
    
    if (filters?.userRole) {
      users = users.filter(u => u.userRole === filters.userRole);
    }
    if (filters?.enabled !== undefined) {
      users = users.filter(u => u.enabled === filters.enabled);
    }
    if (filters?.grupo) {
      users = users.filter(u => u.groups.includes(filters.grupo!));
    }
    
    return {
      data: users,
      total: data.total,
      page,
      limit,
      totalPages: Math.ceil(data.total / limit)
    };
  } catch (error) {
    console.error('Error fetching users:', error);
    throw error;
  }
}

export async function getUserById(id: string): Promise<ApiResponse<UserRecord>> {
  try {
    const response = await adminUsersService.getUserById(id);
    const user = mapBffUserToUserRecord(response.data);
    return { data: user, success: true };
  } catch (error) {
    console.error('Error fetching user:', error);
    throw error;
  }
}

export async function createUser(userData: Omit<UserRecord, 'id'> & { groupName?: string }): Promise<ApiResponse<UserRecord>> {
  try {
    const response = await adminUsersService.preRegisterUser({
      username: userData.username || userData.email.split('@')[0],
      email: userData.email,
      firstName: userData.firstName,
      lastName: userData.lastName,
      userRole: mapDisplayToUserRole(userData.userRole),
      phone: userData.phone || undefined,
      groupName: userData.groupName || userData.groups?.[0] || undefined,
      registrationDate: userData.registrationDate || undefined,
      score: userData.score || undefined,
      adviser: userData.adviser || undefined,
      requestApproved: mapSolicitudToRequestApproved(userData.requestStatus)
    });
    
    const user = mapBffUserToUserRecord(response.data);
    return { data: user, success: true, message: 'Usuario creado exitosamente. Se envió email para establecer contraseña.' };
  } catch (error) {
    console.error('Error creating user:', error);
    throw error;
  }
}

export async function updateUser(id: string, userData: Partial<UserRecord> & { groupId?: string }): Promise<ApiResponse<UserRecord>> {
  try {
    const response = await adminUsersService.updateUser(id, {
      firstName: userData.firstName || '',
      lastName: userData.lastName || '',
      email: userData.email,
      userRole: userData.userRole ? mapDisplayToUserRole(userData.userRole) : undefined,
      phone: userData.phone || undefined,
      enabled: userData.enabled,
      registrationDate: userData.registrationDate || undefined,
      score: userData.score,
      adviser: userData.adviser || undefined,
      requestApproved: userData.requestStatus ? mapSolicitudToRequestApproved(userData.requestStatus) : undefined,
      groupId: userData.groupId || undefined
    });
    
    const user = mapBffUserToUserRecord(response.data);
    return { data: user, success: true, message: 'Usuario actualizado exitosamente' };
  } catch (error) {
    console.error('Error updating user:', error);
    throw error;
  }
}

export async function deleteUser(id: string): Promise<ApiResponse<null>> {
  try {
    await adminUsersService.deleteUser(id);
    return { data: null, success: true, message: 'Usuario eliminado exitosamente' };
  } catch (error) {
    console.error('Error deleting user:', error);
    throw error;
  }
}

export async function blockUser(id: string): Promise<ApiResponse<null>> {
  try {
    await adminUsersService.blockUser(id);
    return { data: null, success: true, message: 'Usuario bloqueado exitosamente' };
  } catch (error) {
    console.error('Error blocking user:', error);
    throw error;
  }
}

export async function unblockUser(id: string): Promise<ApiResponse<null>> {
  try {
    await adminUsersService.unblockUser(id);
    return { data: null, success: true, message: 'Usuario desbloqueado exitosamente' };
  } catch (error) {
    console.error('Error unblocking user:', error);
    throw error;
  }
}

export async function bulkBlockUsers(userIds: string[]): Promise<ApiResponse<null>> {
  try {
    await adminUsersService.bulkBlockUsers(userIds);
    return { data: null, success: true, message: `${userIds.length} usuarios bloqueados exitosamente` };
  } catch (error) {
    console.error('Error bulk blocking users:', error);
    throw error;
  }
}

export async function bulkUnblockUsers(userIds: string[]): Promise<ApiResponse<null>> {
  try {
    await adminUsersService.bulkUnblockUsers(userIds);
    return { data: null, success: true, message: `${userIds.length} usuarios desbloqueados exitosamente` };
  } catch (error) {
    console.error('Error bulk unblocking users:', error);
    throw error;
  }
}

export async function resetUserPassword(id: string, newPassword: string, temporary: boolean = true): Promise<ApiResponse<null>> {
  try {
    await adminUsersService.resetPassword(id, { newPassword, temporary });
    return { data: null, success: true, message: 'Contraseña reseteada exitosamente' };
  } catch (error) {
    console.error('Error resetting password:', error);
    throw error;
  }
}

export async function sendPasswordResetEmail(id: string): Promise<ApiResponse<null>> {
  try {
    await adminUsersService.sendPasswordResetEmail(id);
    return { data: null, success: true, message: 'Email de reseteo enviado exitosamente' };
  } catch (error) {
    console.error('Error sending reset email:', error);
    throw error;
  }
}

export async function getGroups(
  page: number = 1,
  limit: number = 10,
  filters?: UserGroupFilters
): Promise<PaginatedResponse<UserGroup>> {
  try {
    const response = await groupsService.getAllGroups();
    let groups = response.data.map((g, i) => mapBffGroupToUserGroup(g, i));
    
    if (filters?.search) {
      const search = filters.search.toLowerCase();
      groups = groups.filter(g => 
        g.nombre.toLowerCase().includes(search) ||
        g.descripcion.toLowerCase().includes(search)
      );
    }
    if (filters?.estado) {
      groups = groups.filter(g => g.estado === filters.estado);
    }
    
    const total = groups.length;
    const totalPages = Math.ceil(total / limit);
    const start = (page - 1) * limit;
    const data = groups.slice(start, start + limit);
    
    return { data, total, page, limit, totalPages };
  } catch (error) {
    console.error('Error fetching groups:', error);
    throw error;
  }
}

export async function getGroupById(id: string): Promise<ApiResponse<UserGroup>> {
  try {
    const response = await groupsService.getGroupById(id);
    const group = mapBffGroupToUserGroup(response.data, 0);
    return { data: group, success: true };
  } catch (error) {
    console.error('Error fetching group:', error);
    throw error;
  }
}

export async function createGroup(groupData: { name: string; description?: string; color?: string }): Promise<ApiResponse<UserGroup>> {
  try {
    const attributes: Record<string, string> = {};
    if (groupData.description) attributes.description = groupData.description;
    if (groupData.color) attributes.color = groupData.color;
    
    const response = await groupsService.createGroup({
      name: groupData.name,
      attributes: Object.keys(attributes).length > 0 ? attributes : undefined
    });
    
    const group = mapBffGroupToUserGroup(response.data, 0);
    return { data: group, success: true, message: 'Grupo creado exitosamente' };
  } catch (error) {
    console.error('Error creating group:', error);
    throw error;
  }
}

export async function updateGroup(id: string, groupData: { name?: string; description?: string; color?: string }): Promise<ApiResponse<UserGroup>> {
  try {
    const attributes: Record<string, string> = {};
    if (groupData.description) attributes.description = groupData.description;
    if (groupData.color) attributes.color = groupData.color;
    
    const response = await groupsService.updateGroup(id, {
      name: groupData.name,
      attributes: Object.keys(attributes).length > 0 ? attributes : undefined
    });
    
    const group = mapBffGroupToUserGroup(response.data, 0);
    return { data: group, success: true, message: 'Grupo actualizado exitosamente' };
  } catch (error) {
    console.error('Error updating group:', error);
    throw error;
  }
}

export async function deleteGroup(id: string): Promise<ApiResponse<null>> {
  try {
    await groupsService.deleteGroup(id);
    return { data: null, success: true, message: 'Grupo eliminado exitosamente' };
  } catch (error) {
    console.error('Error deleting group:', error);
    throw error;
  }
}

export async function getGroupMembers(groupId: string): Promise<ApiResponse<GroupMember[]>> {
  try {
    const response = await groupsService.getGroupMembers(groupId);
    const members = response.data.map(mapGroupUserToMember);
    return { data: members, success: true };
  } catch (error) {
    console.error('Error fetching group members:', error);
    throw error;
  }
}

export async function addUserToGroup(groupId: string, userId: string): Promise<ApiResponse<null>> {
  try {
    await groupsService.addUserToGroup(groupId, userId);
    return { data: null, success: true, message: 'Usuario agregado al grupo exitosamente' };
  } catch (error) {
    console.error('Error adding user to group:', error);
    throw error;
  }
}

export async function removeUserFromGroup(groupId: string, userId: string): Promise<ApiResponse<null>> {
  try {
    await groupsService.removeUserFromGroup(groupId, userId);
    return { data: null, success: true, message: 'Usuario removido del grupo exitosamente' };
  } catch (error) {
    console.error('Error removing user from group:', error);
    throw error;
  }
}

const demoInvitedUsers: InvitedUser[] = [
  
];

export async function getInvitedUsers(
  page: number = 1,
  limit: number = 10,
  filters?: InvitedUserFilters
): Promise<PaginatedResponse<InvitedUser>> {
  await simulateDelay();
  
  let filtered = [...demoInvitedUsers];
  
  if (filters?.search) {
    const search = filters.search.toLowerCase();
    filtered = filtered.filter(u => 
      u.nombre.toLowerCase().includes(search) ||
      u.email.toLowerCase().includes(search)
    );
  }
  if (filters?.estado) {
    filtered = filtered.filter(u => u.estatus === filters.estado);
  }
  
  const total = filtered.length;
  const start = (page - 1) * limit;
  
  return {
    data: filtered.slice(start, start + limit),
    total,
    page,
    limit,
    totalPages: Math.ceil(total / limit)
  };
}

export async function sendInvitation(data: Omit<InvitedUser, 'id' | 'estatus'>): Promise<ApiResponse<InvitedUser>> {
  await simulateDelay();
  const newInvite: InvitedUser = { ...data, id: String(Date.now()), estatus: 'Invitado sin registrar' };
  return { data: newInvite, success: true, message: 'Invitación enviada' };
}

export async function resendInvitation(id: string): Promise<ApiResponse<InvitedUser>> {
  await simulateDelay();
  const invite = demoInvitedUsers.find(i => i.id === id);
  if (!invite) throw new Error('Invitación no encontrada');
  return { data: invite, success: true, message: 'Invitación reenviada' };
}

export async function cancelInvitation(id: string): Promise<ApiResponse<null>> {
  await simulateDelay();
  return { data: null, success: true, message: 'Invitación cancelada' };
}

export async function bulkUploadUsers(file: File): Promise<ApiResponse<{ imported: number; errors: number }>> {
  await simulateDelay(2000);
  return { 
    data: { imported: 15, errors: 2 }, 
    success: true, 
    message: '15 usuarios importados, 2 errores' 
  };
}

export async function downloadBulkTemplate(): Promise<Blob> {
  await simulateDelay(500);
  const csv = 'username,email,firstName,lastName,userRole,phone,groupName,registrationDate,score,adviser,requestApproved\njuan.perez,juan@empresa.com,Juan,Pérez,operator,+525512345678,Appy-User,2024-01-15,0,María García,false';
  return new Blob([csv], { type: 'text/csv' });
}

export async function exportUsersToExcel(filters?: UserFilters): Promise<Blob> {
  const { data: users } = await getUsers(1, 100000, filters);
  const headers = ['ID Empleado', 'Nombre', 'Apellido', 'Fecha Registro', 'Email', 'Solicitud', 'Puesto', 'Teléfono', 'Score', 'Grupos', 'Asesor', 'Estado'];
  const rows = users.map(u => [
    u.username, u.firstName, u.lastName, u.registrationDate, u.email, 
    u.requestStatus, u.userRole, u.phone, u.score, 
    u.groups.join(';'), u.adviser, u.enabled ? 'Activo' : 'Inactivo'
  ]);
  const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
  return new Blob([csv], { type: 'text/csv' });
}

function simulateDelay(ms: number = 300): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}